import React, { useState, useRef, useEffect } from 'react'
import {
	Box,
	TextField,
	Paper,
	List,
	ListItem,
	ListItemAvatar,
	ListItemText,
	Avatar,
	IconButton,
	Typography,
	InputAdornment,
	ClickAwayListener,
	CircularProgress,
} from '@mui/material'
import {
	Search as SearchIcon,
	Close as CloseIcon,
	KeyboardArrowDown as ArrowDownIcon,
} from '@mui/icons-material'

export default function SingleSelectSearch({
	onSelect,
	onRemove,
	label,
	placeholder = 'Search and select',
	width = '100%',
	searchPlaceholder = 'Search users...',
	onSearch,
	isLoading = false,
	options = [],
}) {
	const [query, setQuery] = useState('')
	const [isOpen, setIsOpen] = useState(false)
	const [selected, setSelected] = useState(null)
	const inputRef = useRef(null)
	const searchTimeoutRef = useRef(null)

	useEffect(() => {
		if (searchTimeoutRef.current) {
			clearTimeout(searchTimeoutRef.current)
		}

		if (query.trim()) {
			searchTimeoutRef.current = setTimeout(() => {
				onSearch?.(query)
			}, 300)
		} else {
			onSearch?.('')
		}

		return () => {
			if (searchTimeoutRef.current) {
				clearTimeout(searchTimeoutRef.current)
			}
		}
	}, [query])

	const handleSelect = (option) => {
		setSelected(option)
		onSelect?.(option)
		setQuery('')
		setIsOpen(false)
	}

	const handleRemove = () => {
		setSelected(null)
		onRemove?.()
		setQuery('')
		setIsOpen(false)
	}

	const handleInputFocus = () => {
		setIsOpen(true)
	}

	const handleClickAway = () => {
		setIsOpen(false)
		setQuery('')
	}

	const handleInputChange = (e) => {
		setQuery(e.target.value)
		if (!isOpen) setIsOpen(true)
	}

	const displayValue = selected ? selected.name : query

	return (
		<ClickAwayListener onClickAway={handleClickAway}>
			<Box sx={{ width, position: 'relative' }}>
				{/* Label */}
				{label && (
					<Typography
						variant="body1"
						sx={{
							mb: 1,
							color: '#6B7280',
							fontSize: '14px',
							fontWeight: 400,
							textAlign: 'left',
						}}
					>
						{label}
					</Typography>
				)}

				{/* Main Input Field */}
				<TextField
					data-cy="search-button"
					ref={inputRef}
					fullWidth
					value={displayValue}
					onChange={handleInputChange}
					onFocus={handleInputFocus}
					placeholder={
						isOpen || !selected ? searchPlaceholder : placeholder
					}
					variant="outlined"
					InputProps={{
						startAdornment: isOpen ? (
							<InputAdornment position="start">
								<SearchIcon
									sx={{ color: '#9CA3AF', fontSize: 20 }}
								/>
							</InputAdornment>
						) : null,
						endAdornment: (
							<InputAdornment position="end">
								{isLoading ? (
									<CircularProgress
										size={20}
										sx={{ color: '#9CA3AF' }}
									/>
								) : selected ? (
									<IconButton
										size="small"
										onClick={() => {
											handleRemove()
											inputRef.current?.focus()
										}}
										sx={{
											p: 0.5,
											'&:hover': {
												backgroundColor: 'transparent',
											},
										}}
									>
										<CloseIcon
											sx={{
												fontSize: 20,
												color: '#9CA3AF',
											}}
										/>
									</IconButton>
								) : (
									<ArrowDownIcon
										sx={{
											color: '#9CA3AF',
											fontSize: 20,
											transform: isOpen
												? 'rotate(180deg)'
												: 'rotate(0deg)',
											transition: 'transform 0.2s ease',
										}}
									/>
								)}
							</InputAdornment>
						),
					}}
					sx={{
						'& .MuiOutlinedInput-root': {
							backgroundColor: 'white',
							fontSize: '14px',
							fontFamily: 'Inter',
							borderRadius: '8px',
						},
						'& .MuiInputBase-input': {
							padding: '10px 12px !important',
						},
						'& .MuiInputAdornment-root': {
							margin: '0 !important',
						},
						'& fieldset': {
							borderColor: '#DFE4EB',
						},
						'& .MuiOutlinedInput-notchedOutline': {
							borderColor: '#DFE4EB',
						},
						'&:hover .MuiOutlinedInput-notchedOutline': {
							borderColor: '#DFE4EB',
						},
						'&.Mui-focused .MuiOutlinedInput-notchedOutline': {
							borderColor: '#7ca6bd !important',
						},
					}}
				/>

				{/* Dropdown List */}
				{isOpen && (
					<Paper
						elevation={3}
						sx={{
							position: 'absolute',
							top: 'auto',
							left: 0,
							right: 0,
							mt: 0.5,
							zIndex: 1300,
							maxHeight: 250,
							overflow: 'auto',
							border: '1px solid #E5E7EB',
						}}
					>
						<List sx={{ p: 0 }}>
							{isLoading ? (
								<CircularProgress
									size={20}
									sx={{ color: '#9CA3AF' }}
								/>
							) : options?.length > 0 ? (
								options.map((option) => (
									<ListItem
										key={option._id || option.id}
										button
										onClick={() => handleSelect(option)}
										sx={{
											py: 1.5,
											px: 2,
											'&:hover': {
												backgroundColor: '#F9FAFB',
											},
										}}
									>
										<ListItemAvatar sx={{ minWidth: 40 }}>
											<Avatar
												src={
													option.businessProfilePic ||
													option.profilePic
												}
												alt={option.name}
												sx={{ width: 32, height: 32 }}
											/>
										</ListItemAvatar>
										<Box sx={{ flex: 1 }}>
											<ListItemText
												primary={option.name}
												secondary={
													option?.businessAddress ||
													option.email
												}
												primaryTypographyProps={{
													fontSize: '14px',
													fontWeight: 400,
												}}
												secondaryTypographyProps={{
													fontSize: '12px',
													autoCapitalize: 'sentences',
												}}
											/>
											{option.avgRating > 0 && (
												<Box
													sx={{
														display: 'flex',
														gap: 0.5,
														mt: 0.5,
													}}
												>
													{[1, 2, 3, 4, 5].map(
														(star) => (
															<img
																key={star}
																src={
																	star <=
																	Math.round(
																		option.avgRating
																	)
																		? '/images/star.svg'
																		: '/images/star-without-fill.svg'
																}
																alt="star"
																style={{
																	width: 12,
																	height: 12,
																}}
															/>
														)
													)}
													<Typography
														sx={{
															fontSize: '12px',
															ml: 0.5,
														}}
													>
														({option.totalReviews})
													</Typography>
												</Box>
											)}
										</Box>
										{selected?._id === option._id && (
											<Box sx={{ ml: 1 }}>
												<Box
													sx={{
														width: 16,
														height: 16,
														borderRadius: '50%',
														backgroundColor:
															'#10B981',
														display: 'flex',
														alignItems: 'center',
														justifyContent:
															'center',
													}}
												>
													<Box
														sx={{
															width: 6,
															height: 6,
															backgroundColor:
																'white',
															borderRadius: '50%',
														}}
													/>
												</Box>
											</Box>
										)}
									</ListItem>
								))
							) : query ? (
								<ListItem sx={{ py: 2, px: 2 }}>
									<ListItemText
										primary="No users found"
										primaryTypographyProps={{
											fontSize: '14px',
											color: '#9CA3AF',
										}}
									/>
								</ListItem>
							) : (
								<ListItem sx={{ py: 2, px: 2 }}>
									<ListItemText
										primary="Start typing to search"
										primaryTypographyProps={{
											fontSize: '14px',
											color: '#9CA3AF',
										}}
									/>
								</ListItem>
							)}
						</List>
					</Paper>
				)}

				{/* Selected User Display (Optional) */}
				{selected && !isOpen && (
					<Box
						sx={{
							display: 'flex',
							alignItems: 'center',
							gap: 1,
							mt: 2,
							p: 1.5,
							backgroundColor: '#F3F4F6',
							borderRadius: '8px',
							border: '1px solid #E5E7EB',
						}}
					>
						<Avatar
							src={selected.profilePic}
							alt={selected.name}
							sx={{ width: 36, height: 36 }}
						/>
						<Box sx={{ flex: 1 }}>
							<Typography
								sx={{
									fontSize: '14px',
									fontWeight: 500,
									color: '#111827',
								}}
							>
								{selected.name}
							</Typography>
							{selected.email && (
								<Typography
									sx={{
										fontSize: '12px',
										color: '#6B7280',
									}}
								>
									{selected.email}
								</Typography>
							)}
						</Box>
						<IconButton
							size="small"
							onClick={() => handleRemove()}
							sx={{
								'&:hover': {
									backgroundColor: 'rgba(0, 0, 0, 0.04)',
								},
							}}
						>
							<CloseIcon sx={{ fontSize: 18 }} />
						</IconButton>
					</Box>
				)}
			</Box>
		</ClickAwayListener>
	)
}
